import { ReactNode, useMemo } from 'react'
import BannerCardCarousel from '@/components-mobile/home/BannerCardCarousel'
import CharacterHomeItem from '@/app/[lang]/@desktop/_components/CharacterHomeItem'
import StoryHomeItem from '@/app/[lang]/@desktop/_components/StoryHomeItem'
import { GridCell } from '@/components/home/VirtualHomeGrid'
import {
  getHomeBannerMediaKey,
  getHomeFeedItemMediaKey,
} from '@/utils/homeFirstScreenMedia'
import {
  CharacterGetRes,
  CharacterGetResWithStory,
  HomeSorting,
  StoryGetRes,
} from '@/types/character'
import { TagID } from '@/types/tag'
import { Activity } from '@/types/activity'
import type { NavigateSimulatorGameHandler } from '@/hooks/simulator-game/useNavigateSimulatorGame'

// banner 插入间隔（与 page size 解耦，page size 已按列数自适应）
const BANNER_INTERVAL = 40

type FeedItem = CharacterGetRes | StoryGetRes | CharacterGetResWithStory

type Options = {
  /** SWR 拉到的扁平角色/故事列表 */
  characterGetResList: FeedItem[] | undefined
  /** 是否应为可见或仍在加载的 banner 保留稳定槽位 */
  shouldReserveBannerSlot: boolean
  /** 各类 banner 的显隐开关，透传给 BannerCardCarousel */
  banners: {
    showSummerSale: boolean
    showFirstRecharge: boolean
    showNewUser: boolean
    showAnniversaryBanner: boolean
    showHalloweenBanner: boolean
    showNewTagsBanner: boolean
    showBlackFri: boolean
  }
  /** 动态活动 banner 列表 */
  dynamicActivities: Activity[]
  /** 当前排序（作为 fromScreen 埋点 / banner 展示作用域） */
  sorting: HomeSorting
  /** 当前性别筛选（卡片埋点 logparams） */
  sex: string
  /** 当前选中标签（卡片埋点 logparams） */
  selectedTagIds: TagID[] | string[]
  /** 跳转世界模拟器（CharacterHomeItem 需要） */
  onNavigateSimulatorGame: NavigateSimulatorGameHandler
  /** 首屏 RUM：注册媒体元素 ref */
  registerMediaElement: (key: string) => (el: HTMLElement | null) => void
  /** 首屏 RUM：媒体就绪回调工厂 */
  getMediaReadyHandler: (key: string) => () => void
  /**
   * 首屏卡片数（= columns × 一屏行数 prefetchRows）：前 priorityCount 张卡片
   * （滚动前必然可见）媒体提优先级（eager + fetchPriority=high），
   * 直击「卡片媒体全就绪」指标。0（未测量）时不提级，保持全 lazy。
   */
  priorityCount: number
}

/**
 * 构建首页（桌面端）虚拟网格的扁平 cell 列表：把 SWR 的角色/故事列表转成
 * GridCell[]（卡片 cell + 按 BANNER_INTERVAL 间隔插入的 banner cell），供
 * VirtualHomeGrid 按列数切行渲染。banner 作为普通 cell 计入序列，保持其在原扁平
 * 位置上的视觉表现；整列表都没插过 banner 时在末尾兜底插一个。
 *
 * 从 page.tsx 抽出，page 只负责收集入参 + 渲染，cell 构建逻辑集中在此。
 */
export default function useHomeFeedCells({
  characterGetResList,
  shouldReserveBannerSlot,
  banners,
  dynamicActivities,
  sorting,
  sex,
  selectedTagIds,
  onNavigateSimulatorGame,
  registerMediaElement,
  getMediaReadyHandler,
  priorityCount,
}: Options): GridCell[] {
  return useMemo(() => {
    const list: GridCell[] = []
    let inserted = false

    const renderBanner = (bannerMediaKey: string) => (
      <div
        key={bannerMediaKey}
        ref={registerMediaElement(bannerMediaKey)}
        className="relative w-full"
      >
        <BannerCardCarousel
          className="border border-tertiary-border"
          showSummerSale={banners.showSummerSale}
          showFirstRecharge={banners.showFirstRecharge}
          showNewUser={banners.showNewUser}
          showAnniversaryBanner={banners.showAnniversaryBanner}
          showHalloweenBanner={banners.showHalloweenBanner}
          showNewTagsBanner={banners.showNewTagsBanner}
          showBlackFri={banners.showBlackFri}
          dynamicActivities={dynamicActivities}
          fromScreen={sorting}
          onMediaReady={getMediaReadyHandler(bannerMediaKey)}
        />
      </div>
    )

    characterGetResList?.forEach((item, index) => {
      const shouldRenderBanner =
        index >= 2 &&
        (index - 2) % BANNER_INTERVAL === 0 &&
        shouldReserveBannerSlot
      const bannerMediaKey = getHomeBannerMediaKey(index)
      const itemMediaKey = getHomeFeedItemMediaKey(item, index)
      // 首屏卡片（滚动前必然可见）媒体提优先级；banner 不计入 index，
      // 首屏 banner 最多挤掉一个名额，多提一张图代价可忽略。
      const mediaPriority = index < priorityCount

      if (shouldRenderBanner) {
        inserted = true
        list.push({ key: bannerMediaKey, node: renderBanner(bannerMediaKey) })
      }

      let cardKey: string
      let card: ReactNode
      if (
        (item as CharacterGetResWithStory).type === 'story' ||
        'story_id' in item
      ) {
        const story = (
          (item as CharacterGetResWithStory).type === 'story'
            ? (item as CharacterGetResWithStory).data
            : item
        ) as StoryGetRes
        cardKey = `story-${story.story_id}`
        card = (
          <div key={cardKey} ref={registerMediaElement(itemMediaKey)}>
            <StoryHomeItem
              item={story}
              fromScreen={sorting}
              logparams={{ gender: sex, selectedTags: selectedTagIds }}
              onMediaReady={getMediaReadyHandler(itemMediaKey)}
              mediaPriority={mediaPriority}
            />
          </div>
        )
      } else {
        const character = (
          (item as CharacterGetResWithStory).type === 'character'
            ? (item as CharacterGetResWithStory).data
            : item
        ) as CharacterGetRes
        cardKey = `character-${character?.character?.character_id}`
        card = (
          <div key={cardKey} ref={registerMediaElement(itemMediaKey)}>
            <CharacterHomeItem
              character={character.character}
              characterStats={character.stats}
              creator={character.creator}
              fromScreen={sorting}
              logparams={{ gender: sex, selectedTags: selectedTagIds }}
              onNavigateSimulatorGame={onNavigateSimulatorGame}
              onMediaReady={getMediaReadyHandler(itemMediaKey)}
              mediaPriority={mediaPriority}
            />
          </div>
        )
      }
      list.push({ key: cardKey, node: card })
    })

    // 整个列表都没插入过 banner 时，在末尾兜底插入一个
    if (
      !inserted &&
      shouldReserveBannerSlot &&
      (characterGetResList?.length ?? 0) > 0
    ) {
      const fallbackKey = getHomeBannerMediaKey('fallback')
      list.push({ key: fallbackKey, node: renderBanner(fallbackKey) })
    }

    return list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    characterGetResList,
    shouldReserveBannerSlot,
    banners,
    dynamicActivities,
    sorting,
    sex,
    selectedTagIds,
    onNavigateSimulatorGame,
    priorityCount,
  ])
}
